import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Pencil, Trash } from 'phosphor-react';
import { CmsProduct, ShopContent } from '../../../types/cms_shared_types';
import {
  displayProductInformation,
  displaySchablonProductInformation,
  getSchablonPricePerMonth,
} from '../../../helper/cms';
import { Conditional } from '../../Common/Conditional';
import { ProductConfigurationModal } from '../ProductChoice/ProductConfigurationModal/ProductConfigurationModal';
import { apiGetPrice } from '../../../api/api';
import { Product, ProductListActions } from '../../../types/webshop';
import { PriceProduct } from '../../../types/api_shared_types';
import { ShoppingItemDesktop } from './ShoppingItemDesktop';
import { ShoppingItemMobile } from './ShoppingItemMobile';
import { PriceSimulatorContent } from './PriceSimulatorContent';
import { PriceSimulatorHeader } from './PriceSimulatorHeader';
import { debounce } from '@mui/material';
import { useResetConfirmationModal } from '../../../contexts/ResetConfirmationModalContext';

export const caretTransitionVariants = {
  closed: { transformOrigin: 'center', rotate: 0, transition: { duration: 0.2 } },
  open: { transformOrigin: 'center', rotate: 180, transition: { duration: 0.2 } },
};

interface ShoppingProductProps {
  price: string;
  product: CmsProduct;
  selectedProducts: Product[];
  selectedProductsActions: ProductListActions;
  setTotalPriceFetching: (value: boolean) => void;
  withEdit: boolean;
  cmsShopContent: ShopContent;
}

export const ShoppingProduct: FC<ShoppingProductProps> = ({
  price,
  product,
  selectedProducts,
  selectedProductsActions,
  setTotalPriceFetching,
  withEdit,
  cmsShopContent,
}) => {
  const [opened, setOpened] = useState(false);
  const [showProductConfigurationModal, setShowProductConfigurationModal] = useState(false);

  const [simulatedMeasure, setSimulatedMeasure] = useState('');
  const [simulatedTimeMeasure, setSimulatedTimeMeasure] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const requestId = useRef(0);

  const { openResetConfirmationModal } = useResetConfirmationModal();

  const findSelectedProduct = useMemo(
    () =>
      (code: string): Product => {
        const prod = selectedProducts.find((p) => p.code === code);
        if (!prod) {
          throw new Error(`Couldn't find product '${code}'`);
        }
        return prod;
      },
    [selectedProducts],
  );

  const returnProduct = findSelectedProduct(product.code);

  const updatePrice = useMemo(
    () =>
      debounce(async (simulatedMeasure?: string, simulatedTimeMeasure?: string) => {
        if (!product.reporting) {
          return;
        }

        if (simulatedMeasure || simulatedTimeMeasure) {
          setIsFetching(true);

          const tempProduct = findSelectedProduct(product.code);
          const priceProduct: PriceProduct = {
            code: tempProduct.code,
            quantity: simulatedMeasure?.toString() ?? tempProduct.quantity,
            timeQuantity: simulatedTimeMeasure?.toString() ?? tempProduct.timeQuantity,
            musicClass: tempProduct.musicClass,
          };
          requestId.current += 1;
          const id = requestId.current;
          const {
            data: { priceYearInclVat, priceYearExclVat },
          } = await apiGetPrice(priceProduct, selectedProducts);

          if (requestId.current === id) {
            await selectedProductsActions.replace({
              ...tempProduct,
              ...priceProduct,
              name: product.name,
              priceYearInclVat: Math.round(getSchablonPricePerMonth(product, priceYearInclVat)),
              priceYearExclVat: Math.round(getSchablonPricePerMonth(product, priceYearExclVat)),
              schablonQuantity: tempProduct.schablonQuantity,
              schablonTimeQuantity: tempProduct.schablonTimeQuantity,
            });

            setIsFetching(false);
            return;
          }
        }
      }, 400),
    [product, findSelectedProduct, selectedProducts, selectedProductsActions],
  );

  const onClickTrash = () => {
    if (selectedProducts.length === 1) {
      openResetConfirmationModal();
    } else {
      const prodIndex = selectedProducts.findIndex((selectedProduct) => selectedProduct.code === product.code);
      selectedProductsActions.remove(prodIndex);
    }
  };

  useEffect(() => {
    setTotalPriceFetching(isFetching);
  }, [isFetching, setTotalPriceFetching]);

  useEffect(() => {
    setSimulatedMeasure(returnProduct.quantity);
    setSimulatedTimeMeasure(returnProduct.timeQuantity);
  }, [returnProduct]);

  const Icon = () => {
    return (
      <button type="button" className="pl-2 ml-auto">
        <Conditional
          condition={withEdit && !product.reporting}
          trueRender={
            <Pencil onClick={() => setShowProductConfigurationModal(true)} size={32} className="text-sami-green" />
          }
          falseRender={<Trash onClick={onClickTrash} size={32} className="text-sami-red" />}
        />
      </button>
    );
  };

  const priceSimulatorHeader = () => {
    return product.reporting ? (
      <PriceSimulatorHeader
        product={product}
        opened={opened}
        setOpened={setOpened}
        price={price}
        isFetching={isFetching}
        cmsShopContent={cmsShopContent}
      />
    ) : undefined;
  };

  const priceSimulatorContent = () => {
    return product.reporting ? (
      <PriceSimulatorContent
        product={product}
        opened={opened}
        simulatedMeasure={simulatedMeasure}
        setSimulatedMeasure={setSimulatedMeasure}
        simulatedTimeMeasure={simulatedTimeMeasure}
        setSimulatedTimeMeasure={setSimulatedTimeMeasure}
        updatePrice={updatePrice}
      />
    ) : undefined;
  };

  return (
    <div>
      <div className="hidden w-full sm:block">
        <ShoppingItemDesktop
          title={product.name}
          icon={Icon()}
          bottomContent={priceSimulatorContent()}
          details={
            <div>
              <Conditional
                condition={product.isSchablon}
                trueRender={
                  <div className="mt-2 text-sm text-text-gray">
                    {displaySchablonProductInformation(returnProduct, product)}
                  </div>
                }
              />
              <div className="mt-2 text-sm text-text-dark">{displayProductInformation(product, selectedProducts)}</div>
            </div>
          }
          pricing={
            <Conditional
              condition={priceSimulatorHeader() !== undefined}
              trueRender={<>{priceSimulatorHeader()}</>}
              falseRender={
                <div className="text-xs font-bold truncate text-text-gray sm:text-base">
                  {price} {cmsShopContent.priceWithMonthFormat}
                </div>
              }
            />
          }
        />
      </div>

      <div className="block w-full sm:hidden">
        <ShoppingItemMobile
          title={product.name}
          icon={Icon()}
          details={
            <div>
              <Conditional
                condition={product.isSchablon}
                trueRender={
                  <div className="mt-2 text-sm text-text-dark">
                    {displaySchablonProductInformation(returnProduct, product)}
                  </div>
                }
              />
              <div className="mt-2 text-xs text-text-dark">{displayProductInformation(product, selectedProducts)}</div>
            </div>
          }
          pricing={
            <Conditional
              condition={priceSimulatorHeader() === undefined}
              trueRender={
                <div className="pt-2 text-xs font-bold truncate text-text-gray sm:text-base">
                  {price} {cmsShopContent.priceWithMonthFormat}
                </div>
              }
            />
          }
          bottomContent={
            <div>
              <Conditional
                condition={priceSimulatorHeader() !== undefined}
                trueRender={<div className="mt-4 text-end shrink-0">{priceSimulatorHeader()}</div>}
              />

              {priceSimulatorContent()}
            </div>
          }
        />
      </div>

      <ProductConfigurationModal
        cmsProduct={product}
        showModal={showProductConfigurationModal}
        setShowModal={setShowProductConfigurationModal}
        selectedProducts={selectedProducts}
        selectedProductsActions={selectedProductsActions}
        cmsShopContent={cmsShopContent}
      />
    </div>
  );
};
